'use client';

/**
 * VIDÉOS — cours réels YouTube (Foriji / Digdaum / Hifz / Kang)
 * Grille par catégorie + lecteur en modale iframe.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { speakText } from '@/lib/speech';
import PageBanner from './PageBanner';
import { Play, X, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';
import videosData from '@/data/videos.json';

interface VideoEntry { id: string; title: string; cat: string; dur: string; desc: string }
const VIDEOS = videosData as VideoEntry[];

const CATS: { key: string; fr: string; en: string; ar: string }[] = [
  { key: 'all', fr: 'Toutes', en: 'All', ar: 'الكل' },
  { key: 'foriji', fr: 'Foriji — Alphabet', en: 'Foriji — Alphabet', ar: 'الفُرْجِي — الحروف' },
  { key: 'digdaum', fr: 'Digdaum — Tajwid', en: 'Digdaum — Tajwid', ar: 'الدِّدْغَام — التجويد' },
  { key: 'hifz', fr: 'Hifz — Mémorisation', en: 'Hifz — Memorization', ar: 'حِفْظ — الحفظ' },
  { key: 'kang', fr: 'Kang — Tafsir', en: 'Kang — Tafsir', ar: 'كَانْغ — التفسير' },
];

export default function VideosSection() {
  const { uiLang } = useApp();
  const [cat, setCat] = useState('all');
  const [playing, setPlaying] = useState<VideoEntry | null>(null);

  // Échap ferme le lecteur
  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlaying(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing]);

  const lbl = (fr: string, en: string, ar: string) => (uiLang === 'ar' ? ar : uiLang === 'en' ? en : fr);

  const filtered = useMemo(() => (cat === 'all' ? VIDEOS : VIDEOS.filter((v) => v.cat === cat)), [cat]);

  return (
    <>
      {/* Bannière dorée — Vidéos */}
      <PageBanner
        src="/images/banners/videos.png"
        eyebrow="Vidéos — الفِيديو"
        title={lbl('Regardez, apprenez, mémorisez', 'Watch, learn, memorize', 'شاهد، تعلم، احفظ')}
        sub={lbl("Cours vidéo sélectionnés pour chaque niveau de l'Académie",
          'Selected video lessons for every Academy level',
          'دروس فيديو مختارة لكل مستوى من مستويات الأكاديمية')}
      />
      <section id="section-videos" className="relative mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6">
        {/* Filtres catégories */}
      <div className="mt-8 flex flex-wrap justify-center gap-2">
        {CATS.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              cat === c.key ? 'bg-accent text-emerald-950' : 'border border-accent/30 text-foreground hover:bg-accent/10'
            )}
          >
            {uiLang === 'ar' ? c.ar : uiLang === 'en' ? c.en : c.fr}
          </button>
        ))}
      </div>

      {/* Grille */}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((v) => (
          <button
            key={v.id}
            onClick={() => { setPlaying(v); speakText(v.title, 'fr', 1); }}
            className="group overflow-hidden rounded-2xl border border-accent/20 bg-gradient-to-br from-emerald-950/60 to-black/40 text-left transition-all hover:border-accent/60 hover:shadow-[0_0_30px_rgba(212,175,55,0.15)]"
          >
            <div className="relative aspect-video overflow-hidden">
              {/* Vignette YouTube réelle */}
              <img
                src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                alt={v.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/10">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/90 text-emerald-950 shadow-lg transition-transform group-hover:scale-110">
                  <Play className="h-6 w-6 fill-current" />
                </span>
              </div>
              <span className="absolute bottom-2 right-2 rounded bg-black/80 px-2 py-0.5 text-xs font-medium text-white">{v.dur}</span>
              <span className="absolute top-2 left-2 rounded-full bg-accent/90 px-2.5 py-0.5 text-[10px] font-bold uppercase text-emerald-950">{v.cat}</span>
            </div>
            <div className="p-4">
              <p className="flex items-start gap-2 text-sm font-semibold leading-snug text-foreground">
                <Youtube className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                {v.title}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{v.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Modale lecteur */}
      {playing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" onClick={() => setPlaying(null)}>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3">
              <p className="text-sm font-semibold text-foreground">{playing.title}</p>
              <button onClick={() => setPlaying(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-accent hover:bg-accent/30">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="relative aspect-video overflow-hidden rounded-xl border border-accent/40 shadow-[0_0_60px_rgba(212,175,55,0.2)]">
              <iframe
                src={`https://www.youtube.com/embed/${playing.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                title={playing.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{playing.desc}</p>
          </div>
        </div>
      )}
    </section>
    </>
  );
}
